import { ReactNode } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { FileUploader, Upload } from "@/lib/FileUpload/FileUploader";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useCanEditIncident } from "@/app/Incidents/Hooks/useCanEditIncident";
import { addIncidentPhotosMutation } from "@/app/Incidents/GraphQL/addIncidentPhotosMutation";
import {
  IncidentPhoto,
  IncidentRecord,
} from "@/app/Incidents/GraphQL/incidentRecordQuery";
import { IncidentPhotoGrid } from "@/app/Incidents/Components/IncidentPhotoGrid";
import {
  INCIDENT_UPLOAD_MAX_FILES,
  INCIDENT_UPLOAD_MAX_SIZE,
} from "@/app/Incidents/Utils/incidentUploads";

export function IncidentPhotosCard({ incident }: { incident: IncidentRecord }) {
  const t = useTranslations("incident");
  const canAddPhotos = useCanEditIncident(incident);
  const { mutateAsync: addIncidentPhotos } = useGqlMutation(
    addIncidentPhotosMutation,
  );

  async function addPhotos(uploads: Upload[]) {
    await addIncidentPhotos({
      id: incident.id,
      filePaths: uploads.map((upload) => upload.filePath),
    });

    toast.success(t("photos.photosAdded"));
  }

  return (
    <RecordScreenCard title={t("details.photos")}>
      <div className="grid gap-6 lg:grid-cols-3">
        <PhotoColumn
          title={t("photos.supplierPhotos")}
          photos={incident.photos}
        >
          {canAddPhotos ? (
            <div className="space-y-1">
              <p className="text-xs font-medium text-gray-700">
                {t("photos.addPhotos")}
              </p>
              <FileUploader
                compact
                maxFileCount={INCIDENT_UPLOAD_MAX_FILES}
                maxSize={INCIDENT_UPLOAD_MAX_SIZE}
                onUpload={addPhotos}
              />
            </div>
          ) : null}
        </PhotoColumn>
        <PhotoColumn
          title={t("photos.pickupPhotos")}
          photos={incident.departureImages}
        />
        <PhotoColumn
          title={t("photos.deliveryPhotos")}
          photos={incident.deliveryImages}
        />
      </div>
    </RecordScreenCard>
  );
}

function PhotoColumn({
  title,
  photos,
  children,
}: {
  title: string;
  photos: IncidentPhoto[];
  children?: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <h4 className="flex items-baseline justify-between text-sm font-semibold text-gray-900">
        {title}
        <span className="text-xs font-normal text-gray-500">
          {photos.length}
        </span>
      </h4>
      <IncidentPhotoGrid photos={photos} />
      {children}
    </section>
  );
}
